import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
    Box,
    VStack,
    HStack,
    Button,
    Text,
    Slider,
    SliderTrack,
    SliderFilledTrack,
    SliderThumb,
    IconButton,
    Spinner,
    Alert,
    AlertIcon,
    AlertTitle,
    AlertDescription,
} from '@chakra-ui/react';
import { FaPlay, FaPause, FaStepBackward, FaStepForward, FaRedo } from 'react-icons/fa';

/**
 * VideoPlayer component for playing decoded video frames
 * @param {Object} props
 * @param {Object|Array} props.frames - Decoded video frames (array or decoder result object)
 * @param {Object} props.metadata - Video metadata
 * @param {number} props.targetFrameNumber - Target frame number to seek to
 * @param {number} props.targetFrameIndex - Target frame index (from decoder result)
 * @param {number} props.width - Canvas width
 * @param {number} props.height - Canvas height
 */
function VideoPlayer({ frames: framesInput, metadata, targetFrameNumber, targetFrameIndex, width = 640, height = 480 }) {
    // Handle both old format (array) and new format (decoder result object)
    const frames = Array.isArray(framesInput) ? framesInput : framesInput?.frames || [];
    const initialTargetIndex = targetFrameIndex !== undefined ? targetFrameIndex : 0;
    const canvasRef = useRef(null);
    const animationRef = useRef(null);
    const lastFrameTimeRef = useRef(0);
    const offscreenCanvasRef = useRef(null);
    const offscreenCtxRef = useRef(null);
    const [currentFrameIndex, setCurrentFrameIndex] = useState(0);
    const [isPlaying, setIsPlaying] = useState(false);
    const [frameRate] = useState(60); // Default frame rate changed to 60 FPS
    const [error, setError] = useState(null);
    const [playbackSpeed, setPlaybackSpeed] = useState(1); // Playback speed multiplier
    
    // FPS tracking refs
    const frameCountRef = useRef(0);
    const fpsStartTimeRef = useRef(0);
    const droppedFramesRef = useRef(0);
    const renderTimesRef = useRef([]);
    const [actualFps, setActualFps] = useState(0);
    
    // Ref to track current frame index for animation loop
    const currentFrameIndexRef = useRef(0);
    
    // Keep ref in sync with state
    useEffect(() => {
        currentFrameIndexRef.current = currentFrameIndex;
    }, [currentFrameIndex]);

    // Find initial frame index based on target frame number or index
    useEffect(() => {
        if (!frames || frames.length === 0) return;

        let targetIndex = initialTargetIndex;
        
        // If targetFrameIndex is provided, use it directly
        if (targetFrameIndex !== undefined && targetFrameIndex >= 0 && targetFrameIndex < frames.length) {
            targetIndex = targetFrameIndex;
        }
        // Otherwise, find by frame number
        else if (targetFrameNumber !== undefined) {
            // Find frame with matching or closest frame number
            let closestIndex = 0;
            let minDiff = Math.abs(frames[0].frameNumber - targetFrameNumber);

            for (let i = 0; i < frames.length; i++) {
                const diff = Math.abs(frames[i].frameNumber - targetFrameNumber);
                if (diff < minDiff) {
                    minDiff = diff;
                    closestIndex = i;
                }
                if (frames[i].frameNumber === targetFrameNumber) {
                    targetIndex = i;
                    break;
                }
            }

            targetIndex = closestIndex;
        }

        setCurrentFrameIndex(targetIndex);
        renderFrame(targetIndex);
    }, [frames, targetFrameNumber, targetFrameIndex, initialTargetIndex]);

    // Render a single frame to canvas
    const renderFrame = useCallback((frameIndex) => {
        if (!frames || !frames[frameIndex] || !canvasRef.current) return;

        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        const frame = frames[frameIndex];

        // Validate frame properties
        if (!frame || typeof frame !== 'object') {
            console.error('Invalid frame object at index', frameIndex, frame);
            setError(`Invalid frame object at index ${frameIndex}`);
            return;
        }

        // Validate frame dimensions
        if (!frame.width || frame.width <= 0 || !Number.isInteger(frame.width)) {
            console.error('Invalid frame width:', frame.width, 'at index', frameIndex);
            setError(`Invalid frame width: ${frame.width}`);
            return;
        }

        if (!frame.height || frame.height <= 0 || !Number.isInteger(frame.height)) {
            console.error('Invalid frame height:', frame.height, 'at index', frameIndex);
            setError(`Invalid frame height: ${frame.height}`);
            return;
        }

        // Validate frame data
        if (!frame.data) {
            console.error('Frame data is missing at index', frameIndex);
            setError('Frame data is missing');
            return;
        }

        // Check if frame.data is the correct type and size
        const expectedSize = frame.width * frame.height * 4; // RGBA
        if (!(frame.data instanceof Uint8ClampedArray)) {
            console.error('Frame data is not a Uint8ClampedArray:', frame.data.constructor.name);
            setError(`Frame data is not a Uint8ClampedArray: ${frame.data.constructor.name}`);
            return;
        }

        if (frame.data.length !== expectedSize) {
            console.error(`Frame data size mismatch: expected ${expectedSize}, got ${frame.data.length}`);
            setError(`Frame data size mismatch: expected ${expectedSize}, got ${frame.data.length}`);
            return;
        }

        // Set canvas size to match frame
        if (canvas.width !== frame.width || canvas.height !== frame.height) {
            canvas.width = frame.width;
            canvas.height = frame.height;
        }

        // Initialize offscreen canvas if needed
        if (!offscreenCanvasRef.current || 
            offscreenCanvasRef.current.width !== frame.width || 
            offscreenCanvasRef.current.height !== frame.height) {
            offscreenCanvasRef.current = document.createElement('canvas');
            offscreenCanvasRef.current.width = frame.width;
            offscreenCanvasRef.current.height = frame.height;
            offscreenCtxRef.current = offscreenCanvasRef.current.getContext('2d');
        }

        try {
            // Create ImageData from frame RGBA data
            const imageData = new ImageData(frame.data, frame.width, frame.height);
            
            // Step 1: Put image data on offscreen canvas
            offscreenCtxRef.current.putImageData(imageData, 0, 0);
            
            // Step 2: Draw offscreen canvas onto visible canvas (no Y-flip needed)
            ctx.drawImage(offscreenCanvasRef.current, 0, 0);
            
            setError(null); // Clear any previous errors
        } catch (err) {
            console.error('Error rendering frame:', err);
            console.error('Frame details:', {
                width: frame.width,
                height: frame.height,
                dataType: frame.data?.constructor?.name,
                dataLength: frame.data?.length,
                frameNumber: frame.frameNumber,
                frameIndex: frameIndex
            });
            setError('Failed to render frame: ' + err.message);
        }
    }, [frames]);

    // Animation loop for playback
    const animate = useCallback((timestamp) => {
        if (!isPlaying || !frames || frames.length === 0) return;

        const frameInterval = 1000 / frameRate;
        const deltaTime = timestamp - lastFrameTimeRef.current;

        if (deltaTime >= frameInterval) {
            // Calculate how many frames we should advance (adjusted for playback speed)
            const baseFramesToAdvance = Math.floor(deltaTime / frameInterval);
            const framesToAdvance = Math.max(1, Math.floor(baseFramesToAdvance * playbackSpeed));
            
            // Track dropped frames if we're advancing more than 1
            if (framesToAdvance > 1) {
                droppedFramesRef.current += (framesToAdvance - 1);
                console.log(`Dropped ${framesToAdvance - 1} frames. Total dropped: ${droppedFramesRef.current}`);
            }
            
            // Calculate next frame index using ref
            const nextIndex = (currentFrameIndexRef.current + framesToAdvance) % frames.length;
            
            // Measure actual render time
            const renderStartTime = performance.now();
            
            // Render the frame synchronously
            renderFrame(nextIndex);
            
            const renderEndTime = performance.now();
            const renderTime = renderEndTime - renderStartTime;
            
            // Update state after rendering
            setCurrentFrameIndex(nextIndex);
            renderTimesRef.current.push(renderTime);
            
            // Keep only last 60 render times
            if (renderTimesRef.current.length > 60) {
                renderTimesRef.current.shift();
            }
            
            // Fix timing accumulation by incrementing by exact intervals
            lastFrameTimeRef.current += frameInterval * framesToAdvance;
            
            // Track frame count for FPS calculation
            frameCountRef.current += framesToAdvance;
            
            // Calculate actual FPS every 60 frames
            if (frameCountRef.current >= 60) {
                const currentTime = performance.now();
                const elapsed = currentTime - fpsStartTimeRef.current;
                const fps = (frameCountRef.current * 1000) / elapsed;
                setActualFps(Math.round(fps));
                
                // Calculate average render time
                const avgRenderTime = renderTimesRef.current.reduce((a, b) => a + b, 0) / renderTimesRef.current.length;
                const currentFrame = frames[currentFrameIndexRef.current];
                console.log(`FPS: ${fps.toFixed(1)}, Avg render time: ${avgRenderTime.toFixed(2)}ms, Dropped frames: ${droppedFramesRef.current}, Current frame: ${currentFrame?.frameNumber || 'N/A'}`);
                
                // Reset counters
                frameCountRef.current = 0;
                fpsStartTimeRef.current = currentTime;
                droppedFramesRef.current = 0;
            }
        }

        animationRef.current = requestAnimationFrame(animate);
    }, [isPlaying, frames, frameRate, renderFrame, playbackSpeed]);

    // Start/stop playback
    useEffect(() => {
        if (isPlaying) {
            const now = performance.now();
            lastFrameTimeRef.current = now;
            fpsStartTimeRef.current = now;
            frameCountRef.current = 0;
            droppedFramesRef.current = 0;
            renderTimesRef.current = [];
            animationRef.current = requestAnimationFrame(animate);
        } else {
            if (animationRef.current) {
                cancelAnimationFrame(animationRef.current);
                animationRef.current = null;
            }
        }

        return () => {
            if (animationRef.current) {
                cancelAnimationFrame(animationRef.current);
            }
        };
    }, [isPlaying, animate]);

    // Handle frame change via slider
    const handleFrameChange = (value) => {
        setCurrentFrameIndex(value);
        renderFrame(value);
        setIsPlaying(false);
    };

    // Step forward/backward
    const stepFrame = (direction) => {
        const newIndex = Math.max(0, Math.min(frames.length - 1, currentFrameIndex + direction));
        setCurrentFrameIndex(newIndex);
        renderFrame(newIndex);
        setIsPlaying(false);
    };

    // Toggle play/pause
    const togglePlayPause = () => {
        if (!frames || frames.length === 0) return;
        setIsPlaying(!isPlaying);
    };

    // Reset to target frame
    const resetToTarget = () => {
        if (!frames || frames.length === 0 || targetFrameNumber === undefined) return;

        let targetIndex = frames.findIndex(f => f.frameNumber === targetFrameNumber);
        if (targetIndex === -1) {
            // Find closest frame
            let closestIndex = 0;
            let minDiff = Math.abs(frames[0].frameNumber - targetFrameNumber);
            for (let i = 1; i < frames.length; i++) {
                const diff = Math.abs(frames[i].frameNumber - targetFrameNumber);
                if (diff < minDiff) {
                    minDiff = diff;
                    closestIndex = i;
                }
            }
            targetIndex = closestIndex;
        }

        setCurrentFrameIndex(targetIndex);
        renderFrame(targetIndex);
        setIsPlaying(false);
    };

    if (error) {
        return (
            <Alert status="error">
                <AlertIcon />
                <Box>
                    <AlertTitle>Video Player Error</AlertTitle>
                    <AlertDescription>{error}</AlertDescription>
                </Box>
            </Alert>
        );
    }

    if (!frames || frames.length === 0) {
        return (
            <VStack spacing={4} align="center" p={4}>
                <Spinner size="lg" />
                <Text>Loading video frames...</Text>
            </VStack>
        );
    }

    // Validate frames array contains valid frame objects
    const validFrames = frames.filter(f => f && f.width > 0 && f.height > 0 && f.data);
    if (validFrames.length === 0) {
        return (
            <Alert status="error">
                <AlertIcon />
                <Box>
                    <AlertTitle>Video Player Error</AlertTitle>
                    <AlertDescription>
                        No valid frames found. Frames array contains {frames.length} items but none have valid dimensions or data.
                    </AlertDescription>
                </Box>
            </Alert>
        );
    }

    const currentFrame = frames[currentFrameIndex];
    
    // Safety check for current frame
    if (!currentFrame || !currentFrame.width || !currentFrame.height) {
        return (
            <Alert status="error">
                <AlertIcon />
                <Box>
                    <AlertTitle>Video Player Error</AlertTitle>
                    <AlertDescription>
                        Invalid frame at index {currentFrameIndex}. Frame dimensions: {currentFrame?.width || 'undefined'} x {currentFrame?.height || 'undefined'}
                    </AlertDescription>
                </Box>
            </Alert>
        );
    }
    
    const displayWidth = Math.min(width, currentFrame.width);
    const displayHeight = Math.min(height, currentFrame.height);
    const scale = Math.min(displayWidth / currentFrame.width, displayHeight / currentFrame.height);

    return (
        <VStack spacing={4} align="stretch" p={4}>
            {/* Video info */}
            <HStack justify="space-between">
                <Text fontSize="sm">
                    Frame: {currentFrame.frameNumber} ({currentFrameIndex + 1}/{frames.length})
                </Text>
                <Text fontSize="sm">
                    {currentFrame.width}x{currentFrame.height}
                </Text>
            </HStack>

            {/* Canvas */}
            <Box 
                border="1px solid" 
                borderColor="gray.600" 
                borderRadius="md" 
                overflow="hidden"
                width={displayWidth}
                height={displayHeight}
                mx="auto"
            >
                <canvas
                    ref={canvasRef}
                    style={{
                        width: displayWidth,
                        height: displayHeight,
                        imageRendering: scale < 1 ? 'auto' : 'pixelated'
                    }}
                />
            </Box>

            {/* Frame slider */}
            <Box px={2}>
                <Slider
                    value={currentFrameIndex}
                    min={0}
                    max={frames.length - 1}
                    step={1}
                    onChange={handleFrameChange}
                    isDisabled={isPlaying}
                >
                    <SliderTrack>
                        <SliderFilledTrack />
                    </SliderTrack>
                    <SliderThumb />
                </Slider>
            </Box>

            {/* Playback controls */}
            <HStack justify="center" spacing={2}>
                <IconButton
                    icon={<FaStepBackward />}
                    onClick={() => stepFrame(-1)}
                    isDisabled={isPlaying || currentFrameIndex === 0}
                    size="sm"
                    aria-label="Previous frame"
                />
                <IconButton
                    icon={isPlaying ? <FaPause /> : <FaPlay />}
                    onClick={togglePlayPause}
                    size="md"
                    colorScheme="blue"
                    aria-label={isPlaying ? "Pause" : "Play"}
                />
                <IconButton
                    icon={<FaStepForward />}
                    onClick={() => stepFrame(1)}
                    isDisabled={isPlaying || currentFrameIndex === frames.length - 1}
                    size="sm"
                    aria-label="Next frame"
                />
                {targetFrameNumber !== undefined && (
                    <IconButton
                        icon={<FaRedo />}
                        onClick={resetToTarget}
                        isDisabled={isPlaying}
                        size="sm"
                        aria-label="Reset to target frame"
                        title={`Reset to frame ${targetFrameNumber}`}
                    />
                )}
            </HStack>

            {/* Playback speed controls */}
            <HStack justify="center" spacing={1} mt={2}>
                <Text fontSize="xs" mr={2}>Speed:</Text>
                {[0.5, 1, 1.5, 2].map(speed => (
                    <Button
                        key={speed}
                        size="xs"
                        variant={playbackSpeed === speed ? "solid" : "outline"}
                        colorScheme={playbackSpeed === speed ? "blue" : "gray"}
                        onClick={() => setPlaybackSpeed(speed)}
                    >
                        {speed}x
                    </Button>
                ))}
            </HStack>

            {/* Frame rate info */}
            <Text fontSize="xs" textAlign="center" color="gray.500">
                Target: {frameRate} FPS | Actual: {actualFps || '--'} FPS | Speed: {playbackSpeed}x
            </Text>
        </VStack>
    );
}

export default VideoPlayer;